import { create } from "zustand";
import { persist } from "zustand/middleware";
import type { Notification } from "../types";
import { STORAGE_KEYS } from "../constants";

interface NotificationsState {
  notifications: Notification[];
  addNotification: (notification: Notification) => void;
  removeNotification: (id: string) => void;
  markAsRead: (id: string) => void;
  markAllAsRead: () => void;
  clearNotifications: () => void;
  getUnreadCount: () => number;
  getUnreadNotifications: () => Notification[];
}

/**
 * Zustand store for managing notifications
 * Persists data to localStorage automatically
 */
export const useNotificationsStore = create<NotificationsState>()(
  persist(
    (set, get) => ({
      notifications: [],

      addNotification: (notification) => {
        set((state) => {
          // Prevent duplicate notifications
          const exists = state.notifications.some((n) => n.id === notification.id);
          if (exists) return state;

          return {
            notifications: [notification, ...state.notifications],
          };
        });
      },

      removeNotification: (id) => {
        set((state) => ({
          notifications: state.notifications.filter((n) => n.id !== id),
        }));
      },

      markAsRead: (id) => {
        set((state) => ({
          notifications: state.notifications.map((n) =>
            n.id === id ? { ...n, read: true, status: "read" } : n
          ),
        }));
      },

      markAllAsRead: () => {
        set((state) => ({
          notifications: state.notifications.map((n) => ({
            ...n,
            read: true,
            status: "read",
          })),
        }));
      },

      clearNotifications: () => {
        set({ notifications: [] });
      },

      getUnreadCount: () => {
        return get().notifications.filter((n) => !n.read).length;
      },

      getUnreadNotifications: () => {
        return get().notifications.filter((n) => !n.read);
      },
    }),
    {
      name: STORAGE_KEYS.NOTIFICATIONS,
    }
  )
);
